/**
 * window.DeliveryEstimate — the estimated delivery day, computed in the shopper's
 * browser. One engine for every surface that shows the date: the product page
 * (assets/product-info.js) and the quick view (assets/quick-view.js).
 *
 * The rules come from the theme settings (עיצוב > הגדרות > אספקה) through the data
 * attributes snippets/delivery-estimate.liquid prints (part: 'attrs'). The same snippet
 * computes the day in Liquid for the first paint; this recomputes it so a page served
 * from cache, where Liquid's 'now' is stale, still shows the right day.
 *
 * Dates are handled as UTC midnight standing for one civil day in Israel. Stepping
 * with the UTC getters keeps the arithmetic free of DST jumps, and anchoring "today"
 * to Asia/Jerusalem keeps the answer identical for every shopper, wherever the
 * device clock is set.
 */
if (!window.DeliveryEstimate) {
  // Days the couriers do not deliver, by Hebrew date, split the way the settings
  // offer them: the holidays themselves, their eves, and each intermediate period.
  // 21 Tishri (Hoshana Raba) and 20 Nisan are eves, so they are not repeated below.
  const CLOSED = {
    CLOSED_FULL: { Tishri: [1, 2, 10, 15, 22], Nisan: [15, 21], Sivan: [6] },
    CLOSED_EVE: { Elul: [29], Tishri: [9, 14, 21], Nisan: [14, 20], Sivan: [5] },
  };
  const CLOSED_CHOL_SUKKOT = { Tishri: [16, 17, 18, 19, 20] };
  const CLOSED_CHOL_PESACH = { Nisan: [16, 17, 18, 19] };

  /** Which of the tables above a scope turns on, widest last. */
  const SCOPE_TABLES = {
    none: [],
    full: ['CLOSED_FULL'],
    eve: ['CLOSED_FULL', 'CLOSED_EVE'],
  };

  let hebrewFormat;

  /**
   * The merchant's delivery rules, as the Liquid put them on the element. Every
   * value has a fallback, so an element rendered before these settings existed
   * still behaves the way the store did then.
   */
  const rules = (el) => {
    const dates = (value) => String(value || '')
      .split(/[^0-9-]+/)
      .filter((part) => /^\d{4}-\d{2}-\d{2}$/.test(part));

    // 'chol' was a scope value for a short while; it meant both intermediate periods.
    const legacyChol = el.dataset.holidayScope === 'chol';

    return {
      skipWeekend: el.dataset.skipWeekend !== 'false',
      holidayScope: legacyChol ? 'eve' : el.dataset.holidayScope || 'eve',
      skipPurim: el.dataset.skipPurim === 'true',
      skipCholSukkot: legacyChol || el.dataset.skipCholSukkot !== 'false',
      skipCholPesach: legacyChol || el.dataset.skipCholPesach === 'true',
      closedDates: dates(el.dataset.closedDates),
      openDates: dates(el.dataset.openDates),
    };
  };

  /** Today in Israel, as UTC midnight of that civil day. */
  const todayInIsrael = () => {
    try {
      // en-CA formats as YYYY-MM-DD.
      const ymd = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Jerusalem',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
      const parsed = new Date(ymd + 'T00:00:00Z');
      if (!isNaN(parsed)) return parsed;
    } catch (e) {
      // fall through to the device clock
    }
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  };

  /**
   * Holidays are derived from the Hebrew calendar the browser already carries, so
   * no date list has to be maintained. Where the calendar is unavailable the month
   * name never matches these tables and the estimate quietly falls back to skipping
   * weekends only.
   */
  const isIsraeliHoliday = (date, rules) => {
    if (hebrewFormat === undefined) {
      try {
        hebrewFormat = new Intl.DateTimeFormat('en-u-ca-hebrew', {
          timeZone: 'Asia/Jerusalem',
          day: 'numeric',
          month: 'long',
        });
      } catch (e) {
        hebrewFormat = null;
      }
    }
    if (!hebrewFormat) return false;

    let day;
    let month;
    try {
      for (const part of hebrewFormat.formatToParts(date)) {
        if (part.type === 'day') day = parseInt(part.value, 10);
        else if (part.type === 'month') month = part.value;
      }
    } catch (e) {
      return false;
    }
    if (!month || !Number.isFinite(day)) return false;

    // Purim is its own setting: 14 Adar, and in a leap year 14 Adar II — 14 Adar I
    // is Purim Katan, an ordinary working day.
    if (rules.skipPurim && day === 14 && (month === 'Adar' || month === 'Adar II')) return true;

    // The intermediate days are independent toggles: they are not a wider scope,
    // a merchant can close for Sukkot and still ship through Pesach.
    if (rules.skipCholSukkot && (CLOSED_CHOL_SUKKOT[month] || []).indexOf(day) > -1) return true;
    if (rules.skipCholPesach && (CLOSED_CHOL_PESACH[month] || []).indexOf(day) > -1) return true;

    const tables = SCOPE_TABLES[rules.holidayScope] || SCOPE_TABLES.eve;
    if (!tables.length) return false;

    for (const table of tables) {
      if ((CLOSED[table][month] || []).indexOf(day) > -1) return true;
    }

    // Independence Day is 5 Iyar, moved by law off Friday/Saturday (back to Thursday)
    // and off Monday (forward to Tuesday), so it is matched through its weekday.
    if (month === 'Iyar') {
      const weekday = date.getUTCDay();
      if (day === 5 && weekday !== 5 && weekday !== 6 && weekday !== 1) return true;
      if (day === 4 && weekday === 4) return true;
      if (day === 3 && weekday === 4) return true;
      if (day === 6 && weekday === 2) return true;
    }

    return false;
  };

  /**
   * Delivery estimate in real business days, following the store's rules: the
   * weekend, Israeli holidays and any dates the merchant closed are skipped, and
   * a date the merchant marked open overrides all of them.
   */
  const addBusinessDaysFromToday = (businessDays, rules) => {
    const date = todayInIsrael();
    let added = 0;
    let guard = 0;

    // The guard only bounds a pathological loop; 21 business days never needs it.
    while (added < businessDays && guard++ < 400) {
      date.setUTCDate(date.getUTCDate() + 1);

      const iso = date.toISOString().slice(0, 10);
      if (rules.openDates.indexOf(iso) > -1) {
        added++;
        continue;
      }

      const day = date.getUTCDay();
      if (rules.skipWeekend && (day === 5 || day === 6)) continue;
      if (rules.closedDates.indexOf(iso) > -1) continue;
      if (isIsraeliHoliday(date, rules)) continue;
      added++;
    }

    return date;
  };

  // The date carries a civil day in Israel, held at UTC midnight, so it must be
  // formatted in UTC — otherwise a shopper west of Greenwich sees the day before.
  // short drops the year (the quick facts line): 31.08
  const format = (date, short = false) =>
    date.toLocaleDateString('he-IL', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit',
      ...(short ? {} : { year: 'numeric' }),
    });

  /** The day for an element carrying the attrs, or null when it carries none. */
  const forElement = (el) => {
    const days = parseInt(el?.dataset?.businessDays, 10);
    if (!Number.isFinite(days)) return null;
    return addBusinessDaysFromToday(days, rules(el));
  };

  /** Refreshes every [data-business-days] .delivery-date inside root. */
  const apply = (root = document) => {
    root.querySelectorAll('[data-business-days]').forEach((el) => {
      const date = forElement(el);
      const target = el.querySelector('.delivery-date');
      if (date && target) target.textContent = format(date, el.dataset.dateFormat === 'short');
    });
  };

  window.DeliveryEstimate = { rules, todayInIsrael, isIsraeliHoliday, addBusinessDaysFromToday, format, forElement, apply };
}
